import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import NotifyMeApi from "../Api/NotifyMeApi";
import ProductApi from "../Api/ProductApi";
import { formatDate } from "../helper/DateHelper";
import { toast } from "react-toastify";
import { IMAGE_BASE_URL } from "../Config/Index";
import TablePagination from "./TablePagination";
import StandardDatePicker from "./StandardDatePicker";

const NotifyMeListLayer = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await ProductApi.getAll(0, 100);
                if (res.status && res.response.data) {
                    setProducts(res.response.data.map(p => ({ value: p._id, label: p.name })));
                }
            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };
        fetchProducts();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                rowsPerPage,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                productId: selectedProduct?.value || undefined
            };
            const res = await NotifyMeApi.list(params);
            if (res.status) {
                setRequests(res.response.data || []);
                setTotalCount(res.response.total || 0);
            }
        } catch (error) {
            console.error("Error fetching notify requests:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (id) => {
        if (window.confirm("Are you sure you want to remove this request?")) {
            const res = await NotifyMeApi.remove(id);
            if (res.status) {
                toast.success("Request removed successfully");
                fetchRequests();
            } else {
                toast.error("Failed to remove request");
            }
        }
    };

    const handleClearFilters = () => {
        setStartDate("");
        setEndDate("");
        setSelectedProduct(null);
        if (page === 0) fetchRequests();
        else setPage(0);
    };

    useEffect(() => {
        fetchRequests();
    }, [page, rowsPerPage]);

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden shadow-sm border-0">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-20">
                    <h6 className="text-lg fw-bold mb-0 text-primary-600 d-flex align-items-center gap-2">
                        <Icon icon="solar:bell-bing-bold-duotone" className="text-2xl" />
                        Notify Me Requests
                    </h6>
                </div>

                <div className="row g-3 align-items-end">
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold">From Date</label>
                        <StandardDatePicker
                            name="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="form-control radius-8"
                        />
                    </div>
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold">To Date</label>
                        <StandardDatePicker
                            name="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="form-control radius-8"
                        />
                    </div>
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold">Product (Optional)</label>
                        <Select
                            options={products}
                            value={selectedProduct}
                            onChange={setSelectedProduct}
                            placeholder="All Products"
                            styles={selectStyles()}
                            isClearable
                        />
                    </div>
                    <div className="col-xl-auto col-md-3 col-sm-6 d-flex align-items-end gap-2">
                        <button
                            type="button"
                            onClick={() => { setPage(0); fetchRequests(); }}
                            className="btn btn-primary d-flex align-items-center gap-2 radius-8 h-40-px"
                        >
                            Filter
                        </button>
                        <button
                            type="button"
                            onClick={handleClearFilters}
                            className="btn btn-outline-danger d-flex align-items-center gap-2 radius-8 h-40-px text-nowrap"
                        >
                            <Icon icon="solar:filter-remove-bold-duotone" fontSize={20} /> Clear
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr className="bg-neutral-50 text-uppercase">
                                <th scope="col" className="text-center py-16" style={{ width: '80px', color: "black", fontWeight: '600' }}>S.No</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Customer Details</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Product Details</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Attributes</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Requested Date</th>
                                <th scope="col" className="text-center py-16" style={{ color: "black", fontWeight: '600' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-100">
                                        <div className="spinner-border text-primary-600" role="status"></div>
                                    </td>
                                </tr>
                            ) : requests.length > 0 ? (
                                requests.map((item, index) => (
                                    <tr key={item._id}>
                                        <td className="text-center text-sm fw-medium text-secondary-light">
                                            {page * rowsPerPage + index + 1}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                {item.customer ? (
                                                    <>
                                                        <span className="text-sm fw-bold text-secondary-light">{item.customer.fullName}</span>
                                                        <span className="text-xs text-secondary-400 d-flex align-items-center gap-1">
                                                            <Icon icon="solar:phone-bold" className="text-xs" />
                                                            {item.customer.phoneNumber}
                                                        </span>
                                                    </>
                                                ) : item.email ? (
                                                    <span className="text-sm fw-bold text-secondary-light d-flex align-items-center gap-1">
                                                        <Icon icon="solar:letter-bold" className="text-xs" />
                                                        {item.email}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-secondary-400">Guest User ({item.guestId?.slice(-6)})</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                {item.product?.productImage?.path && (
                                                    <img 
                                                        src={`${IMAGE_BASE_URL}/${item.product.productImage.path}`} 
                                                        alt={item.product.name} 
                                                        className="w-40-px h-40-px rounded-circle object-fit-cover"
                                                    />
                                                )}
                                                <div className="d-flex flex-column">
                                                    <span className="text-sm fw-bold text-secondary-light">{item.product?.name || "N/A"}</span>
                                                    <span className="text-xxxxs text-secondary-400 text-truncate" style={{maxWidth: '200px'}}>{item.product?.slug || "N/A"}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {item.combination && item.combination.length > 0 ? (
                                                    item.combination.map((comb, i) => (
                                                        <span key={i} className="badge bg-primary-100 text-primary-600 text-xxxxs">
                                                            {comb.value}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-secondary-400 text-xs">-</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="text-sm text-secondary-light fw-medium">{formatDate(item.createdAt)}</span>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                onClick={() => handleRemove(item._id)}
                                                className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle mx-auto transition-all border-0"
                                                title="Remove Request"
                                                style={{ width: '40px', height: '40px' }}
                                            >
                                                <Icon icon="solar:trash-bin-trash-bold" className="text-xl" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-100 italic text-secondary-400">
                                        No notification requests found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && totalCount > 0 && (
                    <div className="mt-24">
                        <TablePagination
                            currentPage={page}
                            totalPages={Math.ceil(totalCount / rowsPerPage)}
                            onPageChange={(p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value));
                                setPage(0);
                            }}
                            totalRecords={totalCount}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotifyMeListLayer;
